import i18n from '@/plugins/i18n';
import { EPos, ERange } from '@/plugins/podolib/services/charts/gaitline/gaitline';
export enum ESide {
  left = 'left',
  right = 'right',
}
export type CONTAINER = {
  sideLegend: {
    left: POSITION;
    right: POSITION;
  };
  middle: number;
};
export type POSITION = {
  startX: number;
  endX: number;
};

export class DrawSVG {
  // Val
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly type: string;
  private readonly title: string;

  private readonly angles: any = {
    left: 0,
    right: 0,
  };
  private readonly pos: any = {
    legend: {
      startY: 0,
      endY: 0,
    },
    middleX: 0,
    angles: {
      left: 0,
      right: 0,
    },
  };

  constructor(canvas: HTMLCanvasElement, type: string, left: number, right: number, name: string | null) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.type = type;
    this.title = name
      ? i18n.t(`commons.standards.${name}`).toString()
      : i18n.t('commons.standards.angulation').toString();

    // Define
    this.angles.left = left;
    this.angles.right = right;

    this.pos.legend.startY = this.canvas.height * 0.7; // get 70%
    this.pos.legend.endY = this.canvas.height;
    this.pos.middleX = this.canvas.width / 2;
    this.pos.angles.left = this.pos.middleX - this.pos.middleX / 1.75;
    this.pos.angles.right = this.pos.middleX + this.pos.middleX / 1.75;
  }

  async draw() {
    this.clean();
    //
    this.angleText();
    this.angleLine();
    this.contentStructure();
    this.angleImg();
  }

  // Structure
  contentStructure() {
    // Globals
    this.ctx!.lineWidth = 2;
    this.ctx!.strokeStyle = '#606574';
    const paddingAngulationText = 10;
    const marginAngulationText = 25;
    this.ctx!.font = '500 12px Open Sans';
    this.canvas.style.letterSpacing = '2px';

    // Horizontal bar
    this.ctx!.beginPath();
    this.ctx!.moveTo(0, this.pos.legend.startY);
    this.ctx!.lineTo(this.canvas.width, this.pos.legend.startY);
    this.ctx!.stroke();
    // Vertical bar
    this.ctx!.beginPath();
    this.ctx!.moveTo(this.pos.middleX, marginAngulationText);
    this.ctx!.lineTo(this.pos.middleX, this.pos.legend.startY);
    this.ctx!.stroke();

    // Angulation text
    this.ctx!.font = '600 11px Open Sans';
    this.ctx!.textAlign = 'center';
    this.ctx.fillStyle = '#a4adc6';
    this.ctx!.fillText(this.title, this.pos.middleX, paddingAngulationText);
  }
  // Angles
  angleText() {
    // Globals
    const paddingText = 25;
    const valuesBackground = {
      height: 25,
      width: 70,
      yPadding: 10,
    };
    //
    this.ctx!.font = '600 15px Open Sans';
    this.ctx!.textAlign = 'center';

    for (const side of ['left', 'right']) {
      this.ctx!.fillStyle = 'rgba(63,145,222,0.1)';
      this.ctx!.beginPath();
      this.ctx!.rect(
        this.pos.angles[side] - valuesBackground.width / 2,
        this.pos.legend.startY + 20 - valuesBackground.height / 2,
        valuesBackground.width,
        valuesBackground.height,
      );
      this.ctx!.fill();

      // Text
      this.ctx.fillStyle = '#3E91DE';
      this.ctx!.fillText(
        `${parseFloat(this.angles[side].toFixed(1))} °`,
        this.pos.angles[side],
        this.pos.legend.startY + paddingText,
      );
    }
  }
  angleLine() {
    // Globals
    const line = {
      size: 50,
      width: 3,
      marginBottom: 1,
    };
    const leftAngle = Math.abs(this.angles.left);
    const rightAngle = Math.abs(this.angles.right);
    const angles: any = {
      left: {
        x: Math.cos((leftAngle * Math.PI) / 180) * line.size,
        y: Math.sin((leftAngle * Math.PI) / 180) * line.size,
      },
      right: {
        x: Math.cos((rightAngle * Math.PI) / 180) * line.size,
        y: Math.sin((rightAngle * Math.PI) / 180) * line.size,
      },
    };
    const lineX: any = {
      left: this.isNegativ('left') ? this.pos.angles.left + angles.left.x : this.pos.angles.left - angles.left.x,
      right: this.isNegativ('right') ? this.pos.angles.right - angles.right.x : this.pos.angles.right + angles.right.x,
    };
    //
    this.ctx.lineCap = 'round';
    this.ctx!.strokeStyle = '#3E91DE';
    this.ctx!.lineWidth = line.width;
    this.ctx!.fillStyle = 'rgba(63,145,222,0.1)';

    for (const side of ['left', 'right']) {
      // Angle shape
      this.ctx!.beginPath();
      this.ctx!.moveTo(this.pos.angles[side], this.pos.legend.startY - line.marginBottom);
      this.ctx!.lineTo(lineX[side], this.pos.legend.startY - angles[side].y - line.marginBottom);
      this.ctx!.lineTo(lineX[side], this.pos.legend.startY - line.marginBottom);
      this.ctx!.fill();

      // Angle line
      this.ctx!.beginPath();
      this.ctx!.moveTo(this.pos.angles[side], this.pos.legend.startY - line.marginBottom);
      this.ctx!.lineTo(lineX[side], this.pos.legend.startY - angles[side].y - line.marginBottom);
      this.ctx!.stroke();
    }
  }
  angleImg() {
    // GLobals
    const that: any = this;
    const resize = 0.7;
    const imgs: any = {
      heel_strike: {
        img: require('@/assets/images/heel-strike-shoe.svg'),
        height: 69,
        width: 60,
      },
      flat_foot: {
        img: require('@/assets/images/toe-strike-shoe.svg'),
        height: 70,
        width: 80,
      },
      toe_off: {
        img: require('@/assets/images/toe-off-shoe.svg'),
        height: 82,
        width: 50,
      },
    };
    const angleImages = {
      left: {
        img: new Image(),
        imgSrc: imgs[this.type].img,
      },
      right: {
        img: new Image(),
        imgSrc: imgs[this.type].img,
      },
      height: imgs[this.type].height * resize,
      width: imgs[this.type].width * resize,
    };
    that.ctx!.globalCompositeOperation = 'destination-over';

    // left
    angleImages.left.img.onload = function () {
      that.ctx!.drawImage(
        angleImages.left.img,
        that.pos.angles.left - angleImages.width / 2,
        that.pos.legend.startY - angleImages.height,
        angleImages.width,
        angleImages.height,
      );
    };
    angleImages.left.img.src = angleImages.left.imgSrc;

    // right
    angleImages.right.img.onload = function () {
      that.ctx!.save();
      that.ctx!.scale(-1, 1);
      that.ctx!.drawImage(
        angleImages.right.img,
        (that.pos.angles.right + angleImages.width / 2) * -1,
        that.pos.legend.startY - angleImages.height,
        angleImages.width,
        angleImages.height,
      );
      that.ctx!.restore();
    };
    angleImages.right.img.src = angleImages.right.imgSrc;
  }

  isNegativ(side: string) {
    return this.angles[side] < 0;
  }

  clean() {
    this.canvas.style.letterSpacing = '0px';
    // Store the current transformation matrix
    this.ctx!.save();

    // Use the identity matrix while clearing the canvas
    this.ctx!.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx!.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Restore the transform
    this.ctx!.restore();
    this.ctx!.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
